import parse, { Element } from 'html-react-parser';
import { useMemo } from 'react';

export type ShortcodeSerializer = Record<
  string,
  (...args: any[]) => false | void | object | Element | null | undefined
>;

const getShortcodeRegex = (name: string) =>
  new RegExp(`\\[${name}([^\\]]*)\\]`, 'g');
const getShortcodeDetails = (shortcode: string) => {
  const _shortcode = shortcode.replace(/[[\]]/g, '').trim();
  const [name, ...args] = _shortcode.split(/\s/);
  const _args = [
    ...new URLSearchParams(
      args.join(' ').replace(/"\s\w/g, (match) => match.replace(/\s/, '&'))
    ).entries(),
  ];

  return {
    name,
    attributes: _args.reduce<Record<string, string>>(
      (acc, [arg, value]) => ({ ...acc, [arg]: value.slice(1, -1) }),
      {}
    ),
  };
};
const transformShortcodeToNode = (shortcode: string) => {
  const { name, attributes } = getShortcodeDetails(shortcode);
  const attributesString = Object.entries(attributes)
    .map(([key, val]) => `${key}="${val}"`)
    .join(' ');

  return attributesString
    ? `<sc component="${name}" ${attributesString}></sc>`
    : `<sc component="${name}"></sc>`;
};

/**
 * Helper hook for WordPress content.  
 * 
 * ---
 * Parses string content into DOM elements with an option to
 * replace shortcode matches with strings or JSX elements.
 * 
 * @example
 * const content = useShortcodes(
     `<p>Hello [button label="Foo" url="bar.com"] world!</p>`,
     {
       button: ({ label, url }) => <a href={url}>{label}</a>,
     }
   );
 */
export const useShortcodes = (
  rawContent: string,
  serializer: ShortcodeSerializer = {}
) =>
  useMemo(() => {
    let newContent = rawContent;

    // replace shortcodes with fake DOM elements for
    // html-react-parser to recognize them
    Object.keys(serializer).forEach((shortcodeName) => {
      newContent = newContent.replace(
        getShortcodeRegex(shortcodeName),
        (match) => {
          return transformShortcodeToNode(match);
        }
      );
    });

    return parse(newContent, {
      replace(domNode) {
        if (
          domNode instanceof Element &&
          domNode.attribs &&
          domNode.name === 'sc'
        ) {
          try {
            const { component, ...attributes } = domNode.attribs;
            return serializer[component](attributes);
          } catch {
            return domNode;
          }
        }
      },
    });
  }, [rawContent]);
